import * as XLSX from "xlsx";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";

import { db } from "../../../backend/firebaseConfig";

const STAFF_COLLECTION_PATH = ["Authorized Members", "Staff", "Data"];

const LEAVE_TYPES = {
  ANNUAL_LEAVE: "annual_leave",
  SICK_LEAVE: "sick_leave",
  UNPAID_LEAVE: "unpaid_leave",
  PAID_LEAVE: "paid_leave",
  OTHER_LEAVE: "other",
};

const toNumber = (value, fallback = 0) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const cleanText = (value = "") => {
  return String(value || "").trim();
};

const normaliseKey = (value = "") => {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[^a-z0-9 ]/g, "");
};

const getDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const getNextWorkingDate = (date) => {
  const current = new Date(date);

  while (isWeekend(current)) {
    current.setDate(current.getDate() + 1);
  }

  return current;
};

const getWorkingDatesForYear = (year) => {
  const dates = [];
  const current = new Date(year, 0, 1);
  const end = new Date(year, 11, 31);

  while (current <= end) {
    if (!isWeekend(current)) {
      dates.push(new Date(current));
    }

    current.setDate(current.getDate() + 1);
  }

  return dates;
};

const getWeekdayLetter = (date) => {
  const letters = ["S", "M", "T", "W", "T", "F", "S"];
  return letters[date.getDay()];
};

const parseDayNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const number = Number(value);

  if (!Number.isFinite(number)) return null;
  if (number < 1 || number > 31) return null;

  return number;
};

const EXCEL_EPOCH = Date.UTC(1899, 11, 30);

const isExcelDateSerial = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 40000 && number < 50000;
};

const excelSerialToDate = (serial) => {
  const utcDate = new Date(EXCEL_EPOCH + Number(serial) * 86400000);

  return new Date(
    utcDate.getUTCFullYear(),
    utcDate.getUTCMonth(),
    utcDate.getUTCDate(),
  );
};

const parseExcelDateCell = (value) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (isExcelDateSerial(value)) {
    return excelSerialToDate(value);
  }

  return null;
};

const getYearFromFileName = (fileName = "") => {
  const match = String(fileName).match(/20\d{2}/);
  return match ? Number(match[0]) : new Date().getFullYear();
};

const findSheetName = (workbook, type) => {
  const lowerType = type.toLowerCase();

  if (lowerType === "holiday") {
    return (
      workbook.SheetNames.find(
        (name) =>
          name.toLowerCase().includes("holiday") &&
          !name.toLowerCase().includes("summary"),
      ) || workbook.SheetNames[0]
    );
  }

  if (lowerType === "summary") {
    return (
      workbook.SheetNames.find((name) =>
        name.toLowerCase().includes("summary"),
      ) || null
    );
  }

  return workbook.SheetNames[0];
};

const sheetToRows = (worksheet) => {
  return XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: null,
    raw: true,
  });
};

const findNameHeader = (rows) => {
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];

    for (let colIndex = 0; colIndex < row.length; colIndex += 1) {
      if (normaliseKey(row[colIndex]) === "name") {
        return {
          rowIndex,
          colIndex,
        };
      }
    }
  }

  return null;
};

const findHolidaySheetLayout = (workbook) => {
  for (const sheetName of workbook.SheetNames) {
    const worksheet = workbook.Sheets[sheetName];

    if (!worksheet) continue;

    const rows = sheetToRows(worksheet);
    const nameHeader = findNameHeader(rows);

    if (!nameHeader) continue;

    const headerRow = rows[nameHeader.rowIndex] || [];
    const nextRow = rows[nameHeader.rowIndex + 1] || [];

    let excelDateCount = 0;

    for (let col = nameHeader.colIndex + 1; col < headerRow.length; col += 1) {
      if (parseExcelDateCell(headerRow[col])) {
        excelDateCount += 1;
      }
    }

    if (excelDateCount >= 5) {
      return {
        sheetName,
        rows,
        nameHeader,
        layoutType: "excel_date_row",
      };
    }

    let dayNumberCount = 0;

    for (let col = nameHeader.colIndex + 1; col < nextRow.length; col += 1) {
      if (parseDayNumber(nextRow[col])) {
        dayNumberCount += 1;
      }
    }

    if (dayNumberCount >= 5) {
      return {
        sheetName,
        rows,
        nameHeader,
        layoutType: "old_day_number_row",
      };
    }
  }

  throw new Error("Could not find a valid holiday grid in this Excel file.");
};

const buildDateColumnsFromHolidaySheet = ({ rows, year, nameHeader }) => {
  if (!nameHeader) {
    throw new Error("Could not find the Name column in the Holiday sheet.");
  }

  const headerRow = rows[nameHeader.rowIndex] || [];

  const excelDateColumns = [];

  for (let col = nameHeader.colIndex + 1; col < headerRow.length; col += 1) {
    const parsedDate = parseExcelDateCell(headerRow[col]);

    if (!parsedDate) continue;
    if (parsedDate.getFullYear() !== Number(year)) continue;
    if (isWeekend(parsedDate)) continue;

    excelDateColumns.push({
      col,
      date: getDateKey(parsedDate),
    });
  }

  if (excelDateColumns.length > 0) {
    return {
      nameColumnIndex: nameHeader.colIndex,
      firstStaffRowIndex: nameHeader.rowIndex + 1,
      dateColumns: excelDateColumns,
    };
  }

  const weekdayRowIndex = nameHeader.rowIndex;
  const dayRowIndex = nameHeader.rowIndex + 1;

  const weekdayRow = rows[weekdayRowIndex] || [];
  const dayRow = rows[dayRowIndex] || [];

  const candidateColumns = [];

  for (let col = nameHeader.colIndex + 1; col < dayRow.length; col += 1) {
    const dayNumber = parseDayNumber(dayRow[col]);

    if (dayNumber) {
      candidateColumns.push({
        col,
        dayNumber,
        weekdayLetter: cleanText(weekdayRow[col]).charAt(0).toUpperCase(),
      });
    }
  }

  const firstWorkingDate = getNextWorkingDate(new Date(year, 0, 1));
  const firstWorkingDay = firstWorkingDate.getDate();
  const firstWorkingLetter = getWeekdayLetter(firstWorkingDate);

  let startColumnIndex = candidateColumns.findIndex((item) => {
    return (
      item.dayNumber === firstWorkingDay &&
      (!item.weekdayLetter || item.weekdayLetter === firstWorkingLetter)
    );
  });

  if (startColumnIndex === -1) {
    startColumnIndex = candidateColumns.findIndex(
      (item) => item.dayNumber === 1,
    );
  }

  if (startColumnIndex === -1) {
    throw new Error(`Could not find the first working date for ${year}.`);
  }

  const workingDates = getWorkingDatesForYear(year);

  return {
    nameColumnIndex: nameHeader.colIndex,
    firstStaffRowIndex: dayRowIndex + 1,
    dateColumns: candidateColumns
      .slice(startColumnIndex, startColumnIndex + workingDates.length)
      .map((item, index) => ({
        col: item.col,
        date: getDateKey(workingDates[index]),
      })),
  };
};

const getLeaveTypeFromCell = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    if (value > 0) {
      return {
        leaveType: LEAVE_TYPES.ANNUAL_LEAVE,
        dayValue: value >= 1 ? 1 : value,
      };
    }

    return null;
  }

  const raw = String(value).trim();
  const code = raw.toUpperCase().replace(/\s+/g, "");

  if (!code) return null;

  if (code === "1" || code === "AL" || code === "A/L") {
    return {
      leaveType: LEAVE_TYPES.ANNUAL_LEAVE,
      dayValue: 1,
    };
  }

  if (code === "0.5" || code === "1/2" || code === "½" || code === "HALF") {
    return {
      leaveType: LEAVE_TYPES.ANNUAL_LEAVE,
      dayValue: 0.5,
    };
  }

  if (code === "S" || code === "SL" || code === "SICK") {
    return {
      leaveType: LEAVE_TYPES.SICK_LEAVE,
      dayValue: 1,
    };
  }

  if (code === "S0.5" || code === "SICK0.5") {
    return {
      leaveType: LEAVE_TYPES.SICK_LEAVE,
      dayValue: 0.5,
    };
  }

  if (code === "UL" || code === "U/L" || code === "UNPAID") {
    return {
      leaveType: LEAVE_TYPES.UNPAID_LEAVE,
      dayValue: 1,
    };
  }

  if (code === "UL0.5") {
    return {
      leaveType: LEAVE_TYPES.UNPAID_LEAVE,
      dayValue: 0.5,
    };
  }

  if (code === "PL" || code === "P/L" || code === "PAID") {
    return {
      leaveType: LEAVE_TYPES.PAID_LEAVE,
      dayValue: 1,
    };
  }

  if (code === "PL0.5") {
    return {
      leaveType: LEAVE_TYPES.PAID_LEAVE,
      dayValue: 0.5,
    };
  }

  if (code === "A") {
    return {
      leaveType: LEAVE_TYPES.OTHER_LEAVE,
      dayValue: 1,
      reason: "Authorised Absence",
    };
  }

  if (code === "X") {
    return {
      leaveType: LEAVE_TYPES.OTHER_LEAVE,
      dayValue: 1,
      reason: "AWOL",
    };
  }

  return null;
};

const getStaffDisplayName = (staff = {}) => {
  return (
    staff.displayName ||
    staff.name ||
    [staff.firstName, staff.lastName].filter(Boolean).join(" ") ||
    staff.email ||
    ""
  );
};

const getEmailNameKeys = (email = "") => {
  const localPart = String(email).split("@")[0] || "";

  return [
    localPart,
    localPart.replace(/[._-]+/g, " "),
    localPart.split(/[._-]+/)[0],
  ]
    .map(normaliseKey)
    .filter(Boolean);
};

const buildStaffLookup = (staffDirectory = [], manualNameToEmailMap = {}) => {
  const lookup = new Map();

  staffDirectory.forEach((staff) => {
    const email = staff.email || staff.id;

    if (!email) return;

    const displayName = getStaffDisplayName(staff);

    const keys = [
      displayName,
      staff.name,
      staff.firstName,
      [staff.firstName, staff.lastName].filter(Boolean).join(" "),
      ...getEmailNameKeys(email),
    ]
      .map(normaliseKey)
      .filter(Boolean);

    keys.forEach((key) => {
      if (!lookup.has(key)) {
        lookup.set(key, {
          ...staff,
          email,
          displayName,
        });
      }
    });
  });

  Object.entries(manualNameToEmailMap || {}).forEach(([excelName, email]) => {
    const staff = staffDirectory.find((item) => {
      return (item.email || item.id) === email;
    });

    if (staff) {
      lookup.set(normaliseKey(excelName), {
        ...staff,
        email,
        displayName: getStaffDisplayName(staff),
      });
    }
  });

  return lookup;
};

const findSummaryHeader = (rows) => {
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex] || [];

    const hasName = row.some((cell) => normaliseKey(cell) === "name");
    const hasEntitlement = row.some((cell) =>
      normaliseKey(cell).includes("entitlement"),
    );

    if (hasName && hasEntitlement) {
      return rowIndex;
    }
  }

  return -1;
};

const buildSummaryMap = (rows) => {
  const headerRowIndex = findSummaryHeader(rows);

  if (headerRowIndex === -1) return new Map();

  const headerRow = rows[headerRowIndex] || [];

  const findCol = (matchers) => {
    return headerRow.findIndex((cell) => {
      const key = normaliseKey(cell);
      return matchers.some((matcher) => key.includes(matcher));
    });
  };

  const nameCol = findCol(["name"]);
  const entitlementCol = findCol(["entitlement"]);
  const bfExtraCol = findCol(["al bf", "bf extra", "extra"]);
  const takenCol = findCol(["taken"]);
  const remainingCol = findCol(["remaining", "totals"]);
  const notesCol = Math.max(findCol(["notes"]), remainingCol + 1);

  const summaryMap = new Map();

  for (
    let rowIndex = headerRowIndex + 1;
    rowIndex < rows.length;
    rowIndex += 1
  ) {
    const row = rows[rowIndex] || [];
    const name = cleanText(row[nameCol]);

    if (!name) continue;

    const bfExtraRaw = toNumber(row[bfExtraCol], 0);

    summaryMap.set(normaliseKey(name), {
      excelName: name,
      entitlement: toNumber(row[entitlementCol], 0),
      rawBroughtForwardExtra: bfExtraRaw,
      carriedForward: bfExtraRaw > 0 ? bfExtraRaw : 0,
      extraDays: 0,
      borrowedFromNextYear: bfExtraRaw < 0 ? Math.abs(bfExtraRaw) : 0,
      annualLeaveTaken: toNumber(row[takenCol], 0),
      annualLeavePending: 0,
      remaining: toNumber(row[remainingCol], 0),
      notes: cleanText(row[notesCol]),
    });
  }

  return summaryMap;
};

const countEventsByType = (events = []) => {
  return events.reduce(
    (acc, event) => {
      const value = Number(event.dayValue || 0);

      if (event.leaveType === LEAVE_TYPES.ANNUAL_LEAVE) {
        acc.annualLeaveEvents += value;
      }

      if (event.leaveType === LEAVE_TYPES.SICK_LEAVE) {
        acc.sickLeaveTaken += value;
      }

      if (event.leaveType === LEAVE_TYPES.UNPAID_LEAVE) {
        acc.unpaidLeaveTaken += value;
      }

      if (event.leaveType === LEAVE_TYPES.PAID_LEAVE) {
        acc.paidLeaveTaken += value;
      }

      if (event.leaveType === LEAVE_TYPES.OTHER_LEAVE) {
        acc.otherLeaveTaken += value;
      }

      return acc;
    },
    {
      annualLeaveEvents: 0,
      sickLeaveTaken: 0,
      unpaidLeaveTaken: 0,
      paidLeaveTaken: 0,
      otherLeaveTaken: 0,
    },
  );
};

export const fetchStaffHolidayDirectory = async () => {
  const staffRef = collection(db, ...STAFF_COLLECTION_PATH);
  const snapshot = await getDocs(staffRef);

  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      email: String(data.email || "").trim() || docSnap.id,
    };
  });
};

export const fetchStaffHolidayBalance = async ({ staffEmail, year }) => {
  if (!staffEmail || !year) return null;

  const balanceRef = doc(
    db,
    ...STAFF_COLLECTION_PATH,
    staffEmail,
    "LeaveBalances",
    String(year),
  );

  const balanceSnap = await getDoc(balanceRef);

  if (!balanceSnap.exists()) return null;

  return {
    id: balanceSnap.id,
    ...balanceSnap.data(),
  };
};

export const fetchStaffHolidayEvents = async ({ staffEmail, year }) => {
  if (!staffEmail || !year) return [];

  const eventsRef = collection(
    db,
    ...STAFF_COLLECTION_PATH,
    staffEmail,
    "LeaveEvents",
  );

  const snapshot = await getDocs(eventsRef);

  return snapshot.docs
    .map((docSnap) => ({
      id: docSnap.id,
      eventId: docSnap.id,
      ...docSnap.data(),
    }))
    .filter((event) => {
      const eventYear =
        Number(event.importedYear) ||
        Number(String(event.date || "").slice(0, 4));

      return eventYear === Number(year);
    })
    .sort((a, b) => String(a.date).localeCompare(String(b.date)));
};

export const fetchStaffHolidayDirectoryWithBalances = async ({ year }) => {
  const staffDirectory = await fetchStaffHolidayDirectory();

  const enrichedStaff = await Promise.all(
    staffDirectory.map(async (staff) => {
      const staffEmail = staff.email || staff.id;

      const [balance, leaveEvents] = await Promise.all([
        fetchStaffHolidayBalance({
          staffEmail,
          year,
        }),
        fetchStaffHolidayEvents({
          staffEmail,
          year,
        }),
      ]);

      const importedTrackerEvents = (leaveEvents || []).filter((event) => {
        return (
          event.source === "excel_import" ||
          event.importedFrom ||
          event.importedYear
        );
      });

      return {
        ...staff,

        holidayBalance: balance || null,
        leaveBalance: balance || null,
        balance: balance || null,

        leaveEvents,
        trackerEvents: leaveEvents,
        importedTrackerEvents,

        // Keep the contractual full-year allowance separate from a balance's
        // (possibly already prorated) entitlement for the selected year.
        annualLeaveEntitlement:
          staff.annualLeaveEntitlement ??
          staff.annualEntitlement ??
          balance?.annualEntitlement ??
          balance?.fullAnnualEntitlement ??
          balance?.entitlement ??
          20,
      };
    }),
  );

  return enrichedStaff;
};

export const parseHolidayExcelFile = async ({
  file,
  year,
  staffDirectory = [],
  manualNameToEmailMap = {},
  bankHolidays = [],
}) => {
  if (!file) {
    throw new Error("Please select an Excel file.");
  }

  const importYear = Number(year || getYearFromFileName(file.name));

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, {
    type: "array",
    cellDates: false,
  });

  const holidayLayout = findHolidaySheetLayout(workbook);
  const holidaySheetName = holidayLayout.sheetName;
  const holidayRows = holidayLayout.rows;

  const summarySheetName = findSheetName(workbook, "summary");

  const summaryRows = summarySheetName
    ? sheetToRows(workbook.Sheets[summarySheetName])
    : [];

  const staffLookup = buildStaffLookup(staffDirectory, manualNameToEmailMap);

  const bankHolidaySet = new Set(
    bankHolidays.map((holiday) => String(holiday.date)),
  );

  const { nameColumnIndex, firstStaffRowIndex, dateColumns } =
    buildDateColumnsFromHolidaySheet({
      rows: holidayRows,
      year: importYear,
      nameHeader: holidayLayout.nameHeader,
    });

  const summaryMap = buildSummaryMap(summaryRows);

  const staffRows = [];
  const events = [];
  const unmatched = [];
  const skipped = [];

  for (
    let rowIndex = firstStaffRowIndex;
    rowIndex < holidayRows.length;
    rowIndex += 1
  ) {
    const row = holidayRows[rowIndex] || [];
    const excelName = cleanText(row[nameColumnIndex]);
if (!excelName) continue;
if (normaliseKey(excelName) === "template") continue;

    const staffMatch = staffLookup.get(normaliseKey(excelName));

    if (!staffMatch) {
      unmatched.push({
        excelName,
        rowIndex,
      });
      continue;
    }

    const staffEvents = [];

    dateColumns.forEach(({ col, date }) => {
      const parsedLeave = getLeaveTypeFromCell(row[col]);

      if (!parsedLeave) return;

      if (bankHolidaySet.has(date)) {
        skipped.push({
          excelName,
          staffEmail: staffMatch.email,
          date,
          reason: "bank_holiday",
          originalValue: row[col],
        });
        return;
      }

     const event = {
       staffEmail: staffMatch.email,
       staffName: staffMatch.displayName || excelName,
       departmentId: staffMatch.departmentId || "",
       departmentName: staffMatch.departmentName || "",
       date,
       leaveType: parsedLeave.leaveType,
       dayValue: parsedLeave.dayValue,
       dayPart: parsedLeave.dayValue === 0.5 ? "half" : "full",
       reason: parsedLeave.reason || "",
       status: "approved",
       source: "excel_import",
       importedFrom: file.name,
       importedYear: importYear,
     };

      events.push(event);
      staffEvents.push(event);
    });

    const summary = summaryMap.get(normaliseKey(excelName)) || {};
    const eventCounts = countEventsByType(staffEvents);

    staffRows.push({
      excelName,
      staffEmail: staffMatch.email,
      staffName: staffMatch.displayName || excelName,
      departmentId: staffMatch.departmentId || "",
      departmentName: staffMatch.departmentName || "",
      year: importYear,
      eventCount: staffEvents.length,
      eventDays: staffEvents.reduce(
        (total, event) => total + Number(event.dayValue || 0),
        0,
      ),
      balance: {
        year: importYear,
        annualEntitlement: toNumber(staffMatch.annualLeaveEntitlement, 20),
        entitlement: toNumber(
          summary.entitlement,
          toNumber(staffMatch.annualLeaveEntitlement, 20),
        ),
        carriedForward: toNumber(summary.carriedForward, 0),
        extraDays: toNumber(summary.extraDays, 0),
        borrowedFromNextYear: toNumber(summary.borrowedFromNextYear, 0),
        rawBroughtForwardExtra: toNumber(summary.rawBroughtForwardExtra, 0),

        annualLeaveTaken: toNumber(
          summary.annualLeaveTaken,
          eventCounts.annualLeaveEvents,
        ),
        annualLeavePending: 0,

        sickLeaveTaken: eventCounts.sickLeaveTaken,
        unpaidLeaveTaken: eventCounts.unpaidLeaveTaken,
        paidLeaveTaken: eventCounts.paidLeaveTaken,
        otherLeaveTaken: eventCounts.otherLeaveTaken,
        remaining: toNumber(summary.remaining, 0),
        notes: summary.notes || "",
        isProRata: Boolean(summary.notes),
      },
    });
  }

  return {
    fileName: file.name,
    year: importYear,
    holidaySheetName,
    summarySheetName,
    staffRows,
    events,
    unmatched,
    skipped,
    totals: {
      staffMatched: staffRows.length,
      events: events.length,
      unmatched: unmatched.length,
      skipped: skipped.length,
    },
  };
};

const commitInChunks = async (operations) => {
  let batch = writeBatch(db);
  let count = 0;

  for (const operation of operations) {
    operation(batch);
    count += 1;

    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }

  if (count > 0) {
    await batch.commit();
  }
};

const APPROVED_STATUS = "approved";

const DAY_TYPES = {
  FULL: "full",
  MORNING: "morning",
  AFTERNOON: "afternoon",
};

const getImportedRequestDayType = (event = {}) => {
  const dayValue = Number(event.dayValue || 1);

  if (dayValue === 0.5) {
    return DAY_TYPES.MORNING;
  }

  return DAY_TYPES.FULL;
};

const buildImportedIncludedDates = (event = {}) => {
  const dayValue = Number(event.dayValue || 1);
  const dayType = getImportedRequestDayType(event);

  return [
    {
      date: event.date,
      value: dayValue,
      dayValue,
      dayType,
      title: event.leaveType || LEAVE_TYPES.ANNUAL_LEAVE,
      reason: event.reason || "",
    },
  ];
};

const buildImportedRequestAuditSnapshot = (request = {}) => {
  return {
    requestId: request.requestId,
    leaveType: request.leaveType,
    startDate: request.startDate,
    endDate: request.endDate,
    startDayType: request.startDayType,
    endDayType: request.endDayType,
    totalDays: request.totalDays,
    status: request.status,
    staffNotes: request.staffNotes,
    reason: request.reason,
  };
};

const buildImportedAuditEntry = ({ importedBy, request, fileName }) => {
  return {
    action: "imported_and_approved",
    actionedBy: importedBy || "",
    actionedAt: new Date().toISOString(),
    note: `Holiday imported from old holiday tracker: ${fileName}`,
    after: buildImportedRequestAuditSnapshot(request),
  };
};

const getImportedLeaveEventId = ({ parsedImport, requestId, event }) => {
  return ["request", parsedImport.year, event.date, event.leaveType, requestId]
    .filter(Boolean)
    .join("_");
};

export const uploadHolidayExcelImport = async ({
  parsedImport,
  importedBy,
  updateStaffEntitlement = false,
  allowUnmatched = false,
}) => {
  if (!parsedImport) {
    throw new Error("No parsed import data found.");
  }

  if (!allowUnmatched && parsedImport.unmatched?.length > 0) {
    throw new Error("Please match all staff names before uploading.");
  }

  const operations = [];
  const seenRequestKeys = new Set();

  parsedImport.staffRows.forEach((row) => {
    const staffRef = doc(db, ...STAFF_COLLECTION_PATH, row.staffEmail);

    const balanceRef = doc(
      db,
      ...STAFF_COLLECTION_PATH,
      row.staffEmail,
      "LeaveBalances",
      String(row.year),
    );

    operations.push((batch) => {
      batch.set(
        balanceRef,
        {
          ...row.balance,
          importedFrom: parsedImport.fileName,
          importedBy: importedBy || "",
          importedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          updatedBy: importedBy || "",
        },
        { merge: true },
      );
    });

    if (updateStaffEntitlement) {
      operations.push((batch) => {
        batch.set(
          staffRef,
          {
            annualLeaveEntitlement: row.balance.entitlement,
            updatedAt: serverTimestamp(),
            updatedBy: importedBy || "",
          },
          { merge: true },
        );
      });
    }
  });

  parsedImport.events.forEach((event) => {
    if (!event.staffEmail || !event.date || !event.leaveType) return;

    const dayValue = Number(event.dayValue || 1);
    if (!dayValue || dayValue <= 0) return;

    const requestKey = [
      event.staffEmail,
      event.date,
      event.leaveType,
      dayValue,
    ].join("|");

    if (seenRequestKeys.has(requestKey)) return;
    seenRequestKeys.add(requestKey);

    const requestRef = doc(
      collection(
        db,
        ...STAFF_COLLECTION_PATH,
        event.staffEmail,
        "LeaveRequests",
      ),
    );

    const requestId = requestRef.id;
    const dayType = getImportedRequestDayType(event);
    const includedDates = buildImportedIncludedDates(event);

    const requestData = {
      requestId,

      autoApproved: true,
      autoApprovedAt: new Date().toISOString(),
      autoApprovedBy: importedBy || "",

      staffEmail: event.staffEmail,
      staffName: event.staffName || event.staffEmail,

      departments: event.departments || [],
      departmentId: event.departmentId || "",
      departmentName: event.departmentName || "",

      holidayManagers: [],
      holidayManagerEmails: [],
      holidayManagerApprovals: {},
      holidayManagerApprovalPolicy: "any_one",

      leaveType: event.leaveType,
      startDate: event.date,
      endDate: event.date,
      startDayType: dayType,
      endDayType: dayType,

      totalDays: dayValue,
      includedDates,
      excludedDates: [],

      reason: event.reason || "",
      staffNotes: `Imported from old holiday tracker: ${parsedImport.fileName}`,
      status: APPROVED_STATUS,

      adminApproval: {
        status: "approved",
        approvedBy: importedBy || "",
        approvedAt: new Date().toISOString(),
        rejectionReason: "",
      },

      emailPreview: {
        managerSubject: "",
        managerBody: "",
        holidayManagerSubject: "",
        holidayManagerBody: "",
        adminSubject: "",
        adminBody: "",
        staffSubject: "",
        staffBody: "",
      },

      emailStatus: {
        sentToHolidayManagers: false,
        sentToManagers: false,
        sentToAdmins: false,
        sentToStaff: false,
        sentSubmissionToStaff: false,
        sentUpdatedToStaff: false,
      },

      bulkImport: true,
      silentImport: true,
      isTrackerImport: true,
      notificationSuppressed: true,
      calendarSuppressed: true,
      importedFrom: parsedImport.fileName,
      importedYear: parsedImport.year,
      importedSource: "holiday_excel_import",

      createdAt: serverTimestamp(),
      createdBy: importedBy || "",
      updatedAt: serverTimestamp(),
      updatedBy: importedBy || "",
      importedAt: serverTimestamp(),
      importedBy: importedBy || "",
    };

    const auditEntry = buildImportedAuditEntry({
      importedBy,
      request: requestData,
      fileName: parsedImport.fileName,
    });

    const eventId = getImportedLeaveEventId({
      parsedImport,
      requestId,
      event,
    });

    const eventRef = doc(
      db,
      ...STAFF_COLLECTION_PATH,
      event.staffEmail,
      "LeaveEvents",
      eventId,
    );

    const auditRef = doc(
      collection(
        db,
        ...STAFF_COLLECTION_PATH,
        event.staffEmail,
        "HolidayAuditTrail",
      ),
    );

    operations.push((batch) => {
      batch.set(requestRef, {
        ...requestData,
        auditTrail: [auditEntry],
        latestAudit: auditEntry,
      });
    });

    operations.push((batch) => {
      batch.set(
        eventRef,
        {
          eventId,
          requestId,
          staffEmail: event.staffEmail,
          staffName: event.staffName || event.staffEmail,

          date: event.date,
          startDate: event.date,
          endDate: event.date,

          leaveType: event.leaveType,
          dayValue,
          totalDays: dayValue,
          dayPart: event.dayPart || (dayValue === 0.5 ? "half" : "full"),
          status: APPROVED_STATUS,

          source: "approved_request_import",
          bulkImport: true,
          silentImport: true,
          isTrackerImport: true,
          importedFrom: parsedImport.fileName,
          importedYear: parsedImport.year,
          importedSource: "holiday_excel_import",

          createdAt: serverTimestamp(),
          createdBy: importedBy || "",
          importedAt: serverTimestamp(),
          importedBy: importedBy || "",
        },
        { merge: true },
      );
    });

    operations.push((batch) => {
      batch.set(auditRef, {
        ...auditEntry,
        requestId,
        staffEmail: event.staffEmail,
        createdAt: serverTimestamp(),
      });
    });
  });

  await commitInChunks(operations);

  return {
    success: true,
    staffUploaded: parsedImport.staffRows.length,
    eventsUploaded: seenRequestKeys.size,
    requestsUploaded: seenRequestKeys.size,
  };
};
